
import React, { useState, useEffect } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import Layout from "@/components/layout/Layout";
import { CountyHeader } from "@/components/nursing-homes/county/CountyHeader";
import { SearchSection } from "@/components/nursing-homes/county/SearchSection";
import { NursingHomeResults } from "@/components/nursing-homes/county/NursingHomeResults";
import { CountyCTASection } from "@/components/nursing-homes/county/CountyCTASection";
import { useNursingHomes } from "@/hooks/use-nursing-homes";
import { useCounties } from "@/hooks/use-counties";
import { useCountyData } from "@/hooks/use-county-data";
import { SEOHead } from "@/components/seo/SEOHead";
import { generateBreadcrumbSchema, generateFAQSchema, generateHowToSchema, generateLocalBusinessSchema } from "@/lib/schema";
import { BreadcrumbNavigation } from "@/components/ui/breadcrumb-list";
import { CountyIntroduction } from "@/components/nursing-homes/county/CountyIntroduction";
import { CountyFAQSection } from "@/components/nursing-homes/county/CountyFAQSection";
import { CountyAreasSection } from "@/components/nursing-homes/county/CountyAreasSection";
import { CountyNearbySection } from "@/components/nursing-homes/county/CountyNearbySection";
import { getCountyFAQs } from "@/lib/county-faqs";

export default function CountyPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { countySlug } = useParams();
  const [searchQuery, setSearchQuery] = useState("");
  const [sortBy, setSortBy] = useState<string>("name_asc");
  const [selectedCounty, setSelectedCounty] = useState<string | null>(null);
  
  // Reset selected county when navigating to a new county page
  useEffect(() => {
    setSelectedCounty(null);
  }, [location.pathname]);
  
  // Fetch counties for the dropdown
  const { data: counties, isLoading: countiesLoading } = useCounties();
  
  // Fetch specific county data
  const { data: countyData, isLoading: countyDataLoading } = useCountyData(countySlug);
  
  // Format county name from slug
  const formatCountyName = (slug?: string) => {
    if (!slug) return '';
    return slug
      .split('-')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };
  
  const countyName = countyData?.name || formatCountyName(countySlug);
  
  // Fetch nursing homes based on current filters
  const { data: nursingHomes, isLoading, error } = useNursingHomes(
    selectedCounty || countySlug, 
    searchQuery, 
    sortBy
  );

  // Update the URL if the selected county changes through the dropdown
  useEffect(() => {
    if (selectedCounty && selectedCounty !== countySlug) {
      navigate(`/nursing-homes/${selectedCounty}`);
    }
  }, [selectedCounty, navigate, countySlug]);

  // Get county-specific FAQs
  const countyFaqs = getCountyFAQs(countySlug || '');

  // Create breadcrumbs for this page
  const breadcrumbs = [
    { name: "Home", url: "/" },
    { name: "Nursing Homes", url: "/nursing-homes" },
    { name: countyName, url: `/nursing-homes/${countySlug}` }
  ];

  // Get current year for dynamic title
  const currentYear = new Date().getFullYear();

  // Generate schemas for structured data
  const breadcrumbSchema = generateBreadcrumbSchema(breadcrumbs);
  const faqSchema = generateFAQSchema(countyFaqs);
  
  // Add HowTo schema for finding nursing homes process
  const howToSchema = generateHowToSchema({
    name: `How to Find a Nursing Home in ${countyName}`,
    description: `Follow these steps to find the best nursing home in ${countyName} for your loved one.`,
    steps: [
      {
        name: "Research Options",
        text: `Browse our comprehensive list of nursing homes in ${countyName} to understand your options.`
      },
      {
        name: "Compare Facilities",
        text: "Compare amenities, services, and pricing across different nursing homes."
      },
      {
        name: "Check Availability",
        text: "Contact nursing homes directly or use our platform to check current availability."
      },
      {
        name: "Visit in Person",
        text: "Schedule visits to your shortlisted nursing homes to see them firsthand."
      },
      {
        name: "Understand Funding Options",
        text: "Learn about the Fair Deal scheme and other financial options for nursing home care."
      },
      {
        name: "Make Your Decision",
        text: "Choose the nursing home that best meets your loved one's needs and preferences."
      }
    ]
  });
  
  // Add LocalBusiness schema for the county
  const localBusinessSchema = generateLocalBusinessSchema({
    name: `Nursing Homes in ${countyName}`,
    description: `Find and compare quality nursing homes and care facilities in ${countyName}, Ireland.`,
    url: `https://www.grandpal.ie/nursing-homes/${countySlug}`,
    telephone: "+353-1-234-5678",
    address: {
      addressCountry: "Ireland",
      addressRegion: countyName
    }
  });
  
  // Combine all schemas
  const schemas = [breadcrumbSchema, faqSchema, howToSchema, localBusinessSchema];

  // Generate SEO metadata
  const nursingHomeCount = nursingHomes?.length || 0;
  const metaTitle = `Top Nursing Homes in ${countyName} (${currentYear}) | Compare ${nursingHomeCount}+ Homes – Grandpal`;
  
  const metaDescription = `Compare ${nursingHomeCount}+ nursing homes in ${countyName}. Browse reviews, pricing, availability, and HSE Fair Deal eligibility. Let Grandpal help you find the best care for your loved one.`;

  // Format counties for dropdown
  const countyOptions = counties?.map(county => ({
    county: county.name,
    county_slug: county.slug
  })) || [];

  // Create breadcrumb items for the UI component
  const breadcrumbItems = breadcrumbs.map(item => ({
    label: item.name,
    href: item.url
  }));

  // Find nearby counties for cross-linking
  const nearbyCounties = counties?.filter(county => 
    county.slug !== countySlug
  ).slice(0, 4) || [];

  return (
    <Layout>
      <SEOHead 
        title={metaTitle}
        description={metaDescription}
        canonicalUrl={`/nursing-homes/${countySlug}`}
        schema={schemas}
      />
      
      <CountyHeader 
        countyName={countyName} 
        nursingHomeCount={nursingHomeCount}
      />
      
      <div className="container py-12">
        <div className="mb-6">
          <BreadcrumbNavigation items={breadcrumbItems} />
        </div>
        
        <SearchSection 
          searchQuery={searchQuery}
          setSearchQuery={setSearchQuery}
          sortBy={sortBy}
          setSortBy={setSortBy}
          selectedCounty={selectedCounty}
          setSelectedCounty={setSelectedCounty}
          counties={countyOptions}
          countiesLoading={countiesLoading}
        />
        
        <NursingHomeResults 
          isLoading={isLoading}
          error={error as Error}
          nursingHomes={nursingHomes}
          countyName={countyName}
          countySlug={selectedCounty || countySlug || ''}
          searchQuery={searchQuery}
        />
        
        {/* County Introduction moved below nursing home results */}
        <div className="mt-12">
          <CountyIntroduction 
            countyName={countyName} 
            nursingHomeCount={nursingHomeCount}
            description={countyData?.description || undefined}
          />
        </div>
        
        <CountyFAQSection 
          countyName={countyName}
          faqs={countyFaqs}
        />
        
        <CountyAreasSection
          countyName={countyName}
          countySlug={countySlug || ''}
        />
        
        <CountyNearbySection 
          countyName={countyName}
          nearbyCounties={nearbyCounties}
        />
      </div>
      
      <CountyCTASection countyName={countyName} />
    </Layout>
  );
}
